import React, { FC, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Input from "../Input/Input";
import Button from "../Button/Button";
import FeedbackForm from "../feedbackForm/feedbackForm";
import { Spinner } from "../Loader/Loader";
import { useSearch } from "../../hooks/useSearch";
import { CSSTransition } from "react-transition-group";
import { useForm } from "react-hook-form";
import {
    StyledSearchTop,
    StyledSearchMiddle,
    StyledSearchBottom,
    StyledSearchForm,
    StyledSearchFormWrapper,
    StyledSearchResult,
    StyledNoResult,
    StyledNoResultAction,
    StyledActionText,
    StyledLoaderWrapper,
    StyledSearchButtonWrapper,
    StyledSearchText,
    StyledMessageText,
    StyledOverlay,
} from "./searchform.style";
import ReactDOM from "react-dom";
import { SearchSuggestList } from "../searchSuggestList/searchSuggestList";
import { IconSearch } from "../../assets/icons/search";
import { useDebounce } from "../../hooks/useDebounce";
import { Select } from "../Select/Select";
import { partnerId } from "../../api/core";
import { useStates } from "../../hooks/useStates";
import { useCategories } from "../../hooks/useCategories";
import { SearchResultList } from "../searchResultList/SearchResultList";
import { applicationFormUrl, getBooleanQueryParam, qetStringQueryParam } from "../../helpers/utils";
import { useServiceUnavailable } from "../../hooks/useServiceUnavailable";
import { ServiceUnavailable } from "../ServiceUnavailable/ServiceUnavailable";

const SearchForm: FC = (props): JSX.Element => {
    const searchParams = new URLSearchParams(useLocation().search);
    const stateParam = searchParams.get("state");
    const categoryParam = searchParams.get("category");
    const queryParam = searchParams.get("q");
    const defaultQuery = qetStringQueryParam("prefill_query");
    const defaultState = qetStringQueryParam("prefill_state");
    const defaultCategory = qetStringQueryParam("prefill_category");

    const [isSearch, setIsSearch] = useState<boolean>(false);
    const [searchValue, setSearchValue] = useState<string>(
        queryParam || defaultQuery || ""
    );
    const [showedFeedbackForm, setShowedFeedbackForm] = useState<boolean>(false);
    const [pageName, setPageName] = useState<any>("");
    const [isMobile, setIsMobile] = useState<boolean>(false);
    const [state, setState] = useState<string>(stateParam || defaultState || "");
    const [category, setCategory] = useState<string>(
        categoryParam || defaultCategory || ""
    );
    const applicationUrl = applicationFormUrl(window.location.origin);
    const navigate = useNavigate();

    const { control, handleSubmit, watch, reset } = useForm({
        defaultValues: {
            search: searchValue,
            state: state,
            category: category,
        },
    });

    const showCategory = getBooleanQueryParam("show_category", true);
    const showState = getBooleanQueryParam("show_state", true);
    const showResults = getBooleanQueryParam("show_results", false);

    const { data: states } = useStates();
    const { data: categories } = useCategories();
    const isServiceUnavailable = useServiceUnavailable();

    const {
        data: rawData,
        isLoading,
        fetchNextPage,
        hasNextPage,
    } = useSearch(searchValue, state, category);
    const search = watch("search");
    const unfilteredData: any = rawData?.pages.reduce(
        (acc: any, { data }: any) => [...acc, ...data.products],
        []
    ) as any;
    const searchResult = unfilteredData?.reduce((accum: any, item: any) => {
        if (accum.find((current: any) => current?.id === item.id)) return accum;
        return [...accum, item];
    }, []);

    const buttonTypes = {
        suretyss: "secondary",
        default: "primary",
        scs: "secondary",
    };

    useEffect(() => {
        const insert2body = (html: string): void => {
            const div: HTMLDivElement = document.createElement(
                "div"
            ) as HTMLDivElement;
            div.innerHTML = html;
            div.style.display = "none";
            const body: HTMLBodyElement = document.querySelector(
                "body"
            ) as HTMLBodyElement;

            if (body) {
                body.prepend(div);
            }
        };

        (async () => {
            if ("caches" in window) {
                const searchForm = window.document.querySelector("#search-form-script");

                const source = searchForm?.getAttribute("src");

                const sourceUrl = new URL(source ?? "");

                const spriteLink: string = `${sourceUrl.origin}/search-form/sprite.svg`;
                const newCache = await caches.open("sprite");
                const options = {
                    method: "GET",
                    headers: new Headers({
                        Accept: "image/svg+xml",
                    }),
                };
                let response = await newCache.match(spriteLink);
                let html: string | null;

                if (!response) {
                    const req: Request = new Request(spriteLink, {
                        ...options,
                        credentials: "omit",
                    });
                    await newCache.add(req);
                    response = await newCache.match(spriteLink);

                    // @ts-ignore
                    html = await response.text();
                    console.log(html);

                    insert2body(html || "");
                    return;
                }

                html = await response.text();
                insert2body(html);
            }
        })();
    }, []);

    useEffect(() => {
        // Merge q/state/category into existing query params so we don't wipe out
        // other params (utm, partner, etc.) that may be present on page load.
        const params = new URLSearchParams(window.location.search);
        search ? params.set("q", search) : params.delete("q");
        state ? params.set("state", state) : params.delete("state");
        category ? params.set("category", category) : params.delete("category");

        navigate({ search: params.toString() });
    }, [search, state, category, navigate]);

    const debouncedSearch = useDebounce(search, 500);

    useEffect(() => {
        onSubmit(search);
    }, [debouncedSearch]);

    useEffect(() => {
        setIsSearch(true);
    }, [category, state, searchValue]);

    const onSubmit = (val: string) => {
        setSearchValue(() => val.trim());
    };

    const handerResize = () => {
        const isMobile = matchMedia("(max-width: 767px)").matches;
        setIsMobile(isMobile);
    };

    useEffect(() => {
        if (!search?.length && !state && !category) {
            setIsSearch(false);
            setShowedFeedbackForm(false);
        }
    }, [search, category, state, searchResult]);

    useEffect(() => {
        const searchFormElement = document.querySelector(
            "#search-bond"
        ) as HTMLElement;
        handerResize();

        const mutationObserver = new MutationObserver(function (mutations) {
            mutations.forEach(function () {
                setPageName(() => searchFormElement.dataset.pageName);
            });
        });

        mutationObserver.observe(searchFormElement, {
            attributes: true,
        });

        window.addEventListener("resize", handerResize);

        return () => {
            window.removeEventListener("resize", handerResize);
            mutationObserver.disconnect();
        };
    }, []);

    const clearForm = () => {
        reset({ search: "", state: "", category: "" });
        setCategory("");
        setSearchValue("");
        setState("");
    };

    const buttonType = buttonTypes[partnerId as keyof typeof buttonTypes];

    return (
        <StyledSearchForm
            id="search-bond"
            data-page-name=""
            className="bootstrap-styles-widget"
        >
            <StyledSearchFormWrapper
                onSubmit={handleSubmit(({ search }) => onSubmit(search))}
            >
                <Input
                    icon={!isMobile ? "search" : ""}
                    control={control}
                    isSearch
                    isShadow
                    name="search"
                />
                {isSearch &&
                    ReactDOM.createPortal(
                        <StyledOverlay onClick={() => clearForm()} />,
                        document?.body
                    )}
                {!isMobile && (
                    <>
                        {showState && (
                            <Select
                                options={states}
                                onBlur={() => { }}
                                value={state}
                                onChange={(value) => {
                                    setState(value || "");
                                }}
                                placeholder="State (Optional)"
                            />
                        )}
                        {showCategory && (
                            <Select
                                options={categories}
                                onBlur={() => { }}
                                value={category}
                                onChange={(value) => {
                                    setCategory(value || "");
                                }}
                                placeholder="Category (Optional)"
                            />
                        )}
                    </>
                )}
                <StyledSearchButtonWrapper>
                    {isMobile ? (
                        <Button
                            type="submit"
                            isBlock={isMobile}
                            buttonType={buttonType}
                            borderColor="search-button-border"
                            backgroundColor="search-button"
                            textColor="search-button-text"
                        >
                            <IconSearch />
                        </Button>
                    ) : (
                        <Button
                            type="submit"
                            isBlock={isMobile}
                            buttonType={buttonType}
                            borderColor="search-button-border"
                            backgroundColor="search-button"
                            textColor="search-button-text"
                            ariaLabel="Search for a Surety Bond"
                        >
                            Search
                        </Button>
                    )}
                </StyledSearchButtonWrapper>
            </StyledSearchFormWrapper>
            <CSSTransition
                in={isSearch}
                classNames={showResults ? "" : "slideInDown"}
                timeout={500}
                unmountOnExit
            >
                <StyledSearchResult
                    showResults={showResults}
                    resultLength={searchResult?.length || 0}
                >
                    {isServiceUnavailable ? (
                        <ServiceUnavailable isMobile={isMobile} />
                    ) : (
                        <>
                            <StyledSearchTop length={searchResult?.length}>
                                <StyledSearchText size={18} lineHeight="1.16">
                                    {isLoading ? (
                                        <StyledLoaderWrapper>
                                            Searching...
                                            <Spinner />
                                        </StyledLoaderWrapper>
                                    ) : searchResult?.length === 0 ? (
                                        `${
                                          searchResult.length || "0"
                                        } bonds found, please try with a different term.`
                                    ) : (
                                        `${
                                          !showResults
                                            ? `We found ${searchResult?.length || 0} bonds:`
                                            : ""
                                        }`
                                    )}
                                </StyledSearchText>
                            </StyledSearchTop>

                            {searchResult && !!searchResult.length && (
                                <>
                                    {showResults ? (
                                        <SearchResultList
                                            searchResult={searchResult}
                                            pageUrl={applicationUrl}
                                            pageName={pageName}
                                            isMobile={isMobile}
                                        />
                                    ) : (
                                        <StyledSearchMiddle>
                                            <SearchSuggestList
                                                searchResult={searchResult}
                                                pageUrl={applicationUrl}
                                                pageName={pageName}
                                                isMobile={isMobile}
                                            />
                                        </StyledSearchMiddle>
                                    )}
                                    {hasNextPage && (
                                        <div style={{ margin: 15 }}>
                                            <Button
                                                type="button"
                                                isBlock={isMobile}
                                                isDisabled={!hasNextPage}
                                                onClick={() => {
                                                    fetchNextPage();
                                                }}
                                            >
                                                Show more
                                            </Button>
                                        </div>
                                    )}
                                </>
                            )}
                            <StyledSearchBottom>
                                <StyledNoResult resultLength={searchResult?.length || 0}>
                                    <StyledMessageText
                                        size={isMobile ? 14 : 16}
                                        lineHeight="1.5em"
                                        weight={400}
                                    >
                                        Canʼt find the bond you need?
                                    </StyledMessageText>
                                    {!showedFeedbackForm && (
                                        <StyledNoResultAction>
                                            <Button
                                                size="l"
                                                isBlock={isMobile}
                                                onClick={() => setShowedFeedbackForm(true)}
                                            >
                                                Click Here
                                            </Button>
                                            <StyledActionText
                                                size={isMobile ? 14 : 16}
                                                lineHeight="1.5em"
                                                weight={400}
                                            >
                                                to get help from one of our experts.
                                            </StyledActionText>
                                        </StyledNoResultAction>
                                    )}
                                </StyledNoResult>
                                <CSSTransition
                                    in={showedFeedbackForm}
                                    classNames="fadeInDown"
                                    timeout={800}
                                    unmountOnExit
                                >
                                    <FeedbackForm isMobile={isMobile} />
                                </CSSTransition>
                            </StyledSearchBottom>
                        </>
                    )}
                </StyledSearchResult>
            </CSSTransition>
        </StyledSearchForm>
    );
};

export default SearchForm;
